// Auth Service - Authentication API calls

import api, { tokenService } from './api';
import type { LoginCredentials, LoginResponse, User, AuthTokens } from '../types/auth';

// Separate localStorage keys for the admin's original tokens during impersonation
const ADMIN_ACCESS_KEY = 'cm_admin_access_token';
const ADMIN_REFRESH_KEY = 'cm_admin_refresh_token';
// The admin's own profile, so "who is really at the keyboard" survives a
// reload mid-impersonation (the Guru widget binds to that person).
const ADMIN_USER_KEY = 'cm_admin_user';

/** Read identity claims from a JWT payload (no verification: it is our own stashed token). */
function userFromAccessToken(token: string): User | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    if (typeof claims.sub !== 'string' || typeof claims.email !== 'string') return null;
    return normalizeUser({
      id: claims.sub,
      email: claims.email,
      name: claims.email,
      role: claims.role as User['role'],
      status: 'ACTIVE',
      created_at: '',
      last_login_at: null,
      scopes: claims.scopes as string[],
    });
  } catch {
    return null;
  }
}

export const impersonationService = {
  /** Check if we are currently impersonating another user. */
  isImpersonating: (): boolean => {
    return !!localStorage.getItem(ADMIN_ACCESS_KEY);
  },

  /** Stash current admin tokens (and profile) before swapping to impersonated user. */
  stashAdminTokens: (adminUser?: User | null): void => {
    const access = tokenService.getAccessToken();
    const refresh = tokenService.getRefreshToken();
    if (access) localStorage.setItem(ADMIN_ACCESS_KEY, access);
    if (refresh) localStorage.setItem(ADMIN_REFRESH_KEY, refresh);
    if (adminUser) localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(adminUser));
  },

  /**
   * The admin behind the current impersonation session, or null when not
   * impersonating. Falls back to the stashed admin token's claims when no
   * profile was stashed (a session started before the profile was kept).
   */
  getStashedAdmin: (): User | null => {
    const access = localStorage.getItem(ADMIN_ACCESS_KEY);
    if (!access) return null;
    const raw = localStorage.getItem(ADMIN_USER_KEY);
    if (raw) {
      try {
        return normalizeUser(JSON.parse(raw) as User);
      } catch {
        // Corrupt profile: fall through to the token claims.
      }
    }
    return userFromAccessToken(access);
  },

  /** Restore admin tokens and clear impersonation state.
   *  Returns true if tokens were successfully restored. */
  restoreAdminTokens: (): boolean => {
    const access = localStorage.getItem(ADMIN_ACCESS_KEY);
    const refresh = localStorage.getItem(ADMIN_REFRESH_KEY);
    if (access && refresh) {
      tokenService.setTokens({
        access_token: access,
        refresh_token: refresh,
        token_type: 'bearer',
        expires_in: 3600,
      });
      localStorage.removeItem(ADMIN_ACCESS_KEY);
      localStorage.removeItem(ADMIN_REFRESH_KEY);
      localStorage.removeItem(ADMIN_USER_KEY);
      return true;
    }
    return false;
  },

  /** Clear impersonation stash (used on full logout). */
  clearStash: (): void => {
    localStorage.removeItem(ADMIN_ACCESS_KEY);
    localStorage.removeItem(ADMIN_REFRESH_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
  },
};

export interface ImpersonateResponse {
  tokens: AuthTokens;
  user: User;
}

export interface ViewAsUser {
  id: string;
  email: string;
  name: string;
  role: string;
  salesperson_id: string | null;
}

/**
 * Normalise a user payload from the api at the boundary so downstream code can
 * rely on the `User` contract (kanban 90f3b9d8). Every scope predicate in the
 * app (`landingPage.ts`, `AuthContext.hasScope`, the Guru widget metadata)
 * calls `user.scopes.includes(...)`; if `/api/auth/me` or `/auth/login` ever
 * omits `scopes` — a partial outage, a backend regression, a mocked
 * response — that is a TypeError inside the root render and a blank white
 * page for every CSR. Arrays default to empty; nothing else is invented.
 */
export function normalizeUser(user: User): User {
  const raw = (user ?? {}) as Partial<User>;
  return {
    ...(raw as User),
    scopes: Array.isArray(raw.scopes) ? raw.scopes : [],
    assigned_accounts: Array.isArray(raw.assigned_accounts) ? raw.assigned_accounts : undefined,
  };
}

export const authService = {
  /**
   * Login with email and password
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials);
    const { tokens } = response.data;
    tokenService.setTokens(tokens);
    // A fresh login ends any previous impersonation session.
    // Clear stale stash to prevent false isImpersonating detection.
    impersonationService.clearStash();
    return { ...response.data, user: normalizeUser(response.data.user) };
  },

  /**
   * Logout - invalidate tokens on server and clear local storage
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Continue with logout even if server call fails
    } finally {
      tokenService.clearTokens();
      impersonationService.clearStash();
    }
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<User>('/auth/me');
    return normalizeUser(response.data);
  },

  /**
   * Check if user is authenticated (has valid tokens)
   */
  isAuthenticated: (): boolean => {
    return tokenService.hasTokens();
  },

  /**
   * Verify token is still valid by calling /auth/me
   */
  verifyAuth: async (): Promise<User | null> => {
    if (!tokenService.hasTokens()) {
      return null;
    }
    try {
      return await authService.getCurrentUser();
    } catch {
      tokenService.clearTokens();
      return null;
    }
  },

  /**
   * Impersonate a user (admin only).
   * Stashes admin tokens, swaps to target user tokens.
   */
  impersonate: async (userId: string, adminUser?: User | null): Promise<ImpersonateResponse> => {
    // Stash current admin tokens before swapping
    impersonationService.stashAdminTokens(adminUser);

    const response = await api.post<ImpersonateResponse>(
      `/admin/impersonate/${userId}`,
    );
    const { tokens } = response.data;
    tokenService.setTokens(tokens);
    return { ...response.data, user: normalizeUser(response.data.user) };
  },

  /**
   * Stop impersonating — restore admin tokens.
   * Uses getCurrentUser() instead of verifyAuth() so that
   * the axios 401 interceptor can transparently refresh an
   * expired admin access token without clearing all tokens.
   */
  stopImpersonating: async (): Promise<User | null> => {
    impersonationService.restoreAdminTokens();
    try {
      return await authService.getCurrentUser();
    } catch {
      // Token restoration failed even after interceptor refresh attempt.
      // Return null — caller will handle the unauthenticated state.
      return null;
    }
  },

  /**
   * List users available for impersonation (admin only).
   * @param requiredScope If provided, only returns users with this scope.
   */
  listUsersForViewAs: async (requiredScope?: string): Promise<ViewAsUser[]> => {
    const params: Record<string, string> = {};
    if (requiredScope) params.required_scope = requiredScope;
    const response = await api.get<ViewAsUser[]>('/admin/users', { params });
    return response.data;
  },
};
